(function () {
  'use strict';

  var ENDPOINT = '/apps/tior-ai/consultation/apply';
  var REQUEST_TIMEOUT_MS = 8000;
  var DRAFT_STORAGE_KEY = 'tior-contact-inquiry-draft-v2';

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  }

  onReady(function () {
    document.querySelectorAll('form.tior-contact-form').forEach(function (form) {
      var status = form.querySelector('[data-tior-contact-status]');
      var recovery = form.querySelector('[data-tior-contact-recovery]');
      var retry = form.querySelector('[data-tior-contact-retry]');
      var fallback = form.querySelector('[data-tior-contact-fallback]');
      var submit = form.querySelector('button[type="submit"]');
      var topic = form.querySelector('#ContactForm-topic');
      var consultation = form.querySelector('[data-tior-contact-consultation]');
      var submitLabel = submit.querySelector('span') ? submit.querySelector('span').textContent : 'Send';
      var activeIdempotencyKey = '';
      var inquiryContext = getInquiryContext();
      var draftStorageKey = DRAFT_STORAGE_KEY + ':' + inquiryContext.requestType + ':' + inquiryContext.source;

      configureTopic();
      updateConsultationFields();

      if (inquiryContext.requestType === 'PRODUCT_INQUIRY' && inquiryContext.products.length) {
        var phoneField = form.querySelector('#ContactForm-phone');
        if (phoneField) {
          phoneField.required = true;
          phoneField.setAttribute('aria-required', 'true');
        }
      }

      if (status.dataset.tiorPostedSuccessfully === 'true') {
        clearDraft();
        return;
      }

      restoreDraft();
      updateConsultationFields();
      form.addEventListener('input', saveDraft);
      topic.addEventListener('change', function () {
        updateConsultationFields();
        saveDraft();
      });
      retry.addEventListener('click', function () {
        recovery.hidden = true;
        form.requestSubmit();
      });
      fallback.addEventListener('click', function () {
        status.textContent = 'Sending through Shopify Contact…';
        form.dataset.tiorNativeSubmitting = 'true';
        saveDraft();
        HTMLFormElement.prototype.submit.call(form);
      });

      form.addEventListener('submit', async function (event) {
        if (form.dataset.tiorNativeSubmitting === 'true') return;
        event.preventDefault();
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        activeIdempotencyKey = activeIdempotencyKey || createIdempotencyKey('contact-inquiry');
        saveDraft();
        setSubmitting(true);
        recovery.hidden = true;
        status.textContent = 'Sending your message securely…';

        try {
          var response = await sendInquiry(buildPayload(form, activeIdempotencyKey));
          var body = await response.json().catch(function () { return {}; });
          if (!response.ok) throw new Error(body.message || 'Unable to send your message.');
          clearDraft();
          form.reset();
          configureTopic();
          updateConsultationFields();
          status.textContent = body.data && body.data.alreadySubmitted
            ? 'This message was already received. We will get back to you soon.'
            : 'Thank you. Your message has been received.';
        } catch (error) {
          saveDraft();
          status.textContent = 'Your draft is saved. Try again, or send it through Shopify Contact.';
          recovery.hidden = false;
        } finally {
          setSubmitting(false);
        }
      });

      function buildPayload(targetForm, idempotencyKey) {
        var name = readValue(targetForm, '#ContactForm-name');
        var email = readValue(targetForm, '#ContactForm-email');
        var phone = readValue(targetForm, '#ContactForm-phone');
        var message = readValue(targetForm, '#ContactForm-body');
        var classification = getSelectedTopicClassification();
        var isConsultation = classification.requestType === 'AI_CONSULTATION';
        var useEntrySource = inquiryContext.topic === String(topic && topic.value || '') && inquiryContext.source !== 'ONLINE_STORE_CONTACT_FORM';
        return {
          idempotencyKey: idempotencyKey,
          requestType: inquiryContext.locked ? inquiryContext.requestType : classification.requestType,
          source: inquiryContext.locked || useEntrySource ? inquiryContext.source : classification.source,
          projectType: isConsultation ? readCheckedValue('Project type') : classification.projectType,
          roomType: isConsultation ? readCheckedValue('Space type') : '',
          budget: isConsultation ? readCheckedValue('Budget') : '',
          timeline: '',
          name: name || email.split('@')[0] || 'Storefront visitor',
          email: email,
          phone: phone,
          message: message,
          pageUrl: window.location.href,
          products: inquiryContext.products,
          customer: { name: name, email: email, phone: phone }
        };
      }

      function sendInquiry(payload) {
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        var timeoutId = window.setTimeout(function () {
          if (controller) controller.abort();
        }, REQUEST_TIMEOUT_MS);
        return fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
          signal: controller ? controller.signal : undefined
        }).finally(function () {
          window.clearTimeout(timeoutId);
        });
      }

      function setSubmitting(isSubmitting) {
        submit.disabled = isSubmitting;
        submit.setAttribute('aria-busy', String(isSubmitting));
        var label = submit.querySelector('span');
        if (label) label.textContent = isSubmitting ? 'Sending…' : submitLabel;
      }

      function saveDraft() {
        try {
          window.localStorage.setItem(draftStorageKey, JSON.stringify({
            idempotencyKey: activeIdempotencyKey,
            name: readValue(form, '#ContactForm-name'),
            email: readValue(form, '#ContactForm-email'),
            phone: readValue(form, '#ContactForm-phone'),
            message: readValue(form, '#ContactForm-body'),
            topic: topic && !topic.disabled ? topic.value : '',
            projectType: readCheckedValue('Project type'),
            spaceType: readCheckedValue('Space type'),
            budget: readCheckedValue('Budget'),
            savedAt: Date.now()
          }));
        } catch (error) {}
      }

      function restoreDraft() {
        try {
          var draft = JSON.parse(window.localStorage.getItem(draftStorageKey) || 'null');
          if (!draft) return;
          setValue(form, '#ContactForm-name', draft.name);
          setValue(form, '#ContactForm-email', draft.email);
          setValue(form, '#ContactForm-phone', draft.phone);
          setValue(form, '#ContactForm-body', draft.message);
          if (topic && !topic.disabled && draft.topic) topic.value = String(draft.topic);
          setCheckedValue('Project type', draft.projectType);
          setCheckedValue('Space type', draft.spaceType || draft.roomType);
          setCheckedValue('Budget', draft.budget);
          activeIdempotencyKey = String(draft.idempotencyKey || '');
          if (draft.message || draft.email) status.textContent = 'Your saved draft has been restored.';
        } catch (error) {}
      }

      function clearDraft() {
        activeIdempotencyKey = '';
        try { window.localStorage.removeItem(draftStorageKey); } catch (error) {}
      }

      function configureTopic() {
        if (!topic) return;
        if (inquiryContext.topic) topic.value = inquiryContext.topic;
        setCheckedValue('Project type', inquiryContext.projectType);
        if (inquiryContext.locked) {
          topic.disabled = true;
          topic.setAttribute('aria-describedby', 'tior-contact-topic-context');
          var help = topic.parentElement && topic.parentElement.querySelector('p');
          if (help) {
            help.id = 'tior-contact-topic-context';
            help.textContent = 'This topic was selected from the page where you started your inquiry.';
          }
        }
      }

      function updateConsultationFields() {
        if (!consultation) return;
        var isConsultation = String(topic && topic.value || '').toUpperCase() === 'CONSULTATION';
        consultation.hidden = !isConsultation;
        consultation.querySelectorAll('input[type="radio"]').forEach(function (input) {
          input.required = isConsultation;
          input.disabled = !isConsultation;
        });
      }

      function readCheckedValue(name) {
        var checked = form.querySelector('input[name="contact[' + name + ']"]:checked');
        return String(checked && checked.value || '').trim();
      }

      function setCheckedValue(name, value) {
        if (!value) return;
        form.querySelectorAll('input[name="contact[' + name + ']"]').forEach(function (input) {
          input.checked = input.value === String(value);
        });
      }

      function getSelectedTopicClassification() {
        var value = String(topic && topic.value || 'OTHER').toUpperCase();
        if (value === 'FURNITURE_PRODUCT') {
          return {requestType: 'PRODUCT_INQUIRY', source: 'CONTACT_PRODUCT_INQUIRY', projectType: 'Product inquiry'};
        }
        if (value === 'CONSULTATION') {
          return {requestType: 'AI_CONSULTATION', source: 'CONTACT_CONSULTATION_INQUIRY', projectType: 'Interior consultation'};
        }
        var generalTopics = {
          DELIVERY_INSTALLATION: {source: 'CONTACT_DELIVERY_INSTALLATION', projectType: 'Delivery & installation inquiry'},
          OTHER: {source: 'CONTACT_OTHER_INQUIRY', projectType: 'Other inquiry'},
        };
        var generalTopic = generalTopics[value] || generalTopics.OTHER;
        return {requestType: 'GENERAL_INQUIRY', source: generalTopic.source, projectType: generalTopic.projectType};
      }
    });
  });

  function readValue(form, selector) {
    var field = form.querySelector(selector);
    return String(field && field.value || '').trim();
  }

  function setValue(form, selector, value) {
    var field = form.querySelector(selector);
    if (field && value) field.value = String(value);
  }

  function createIdempotencyKey(prefix) {
    var randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2);
    return prefix + ':' + randomPart;
  }

  function getInquiryContext() {
    var params = new URLSearchParams(window.location.search);
    var source = cleanParam(params.get('source'), 120) || 'ONLINE_STORE_CONTACT_FORM';
    var normalizedSource = source.toLowerCase();
    var inquiryType = cleanParam(params.get('inquiryType'), 120).toLowerCase();
    var explicitType = cleanParam(params.get('requestType'), 120).toUpperCase();
    if (['PRODUCT_INQUIRY', 'AI_CONSULTATION', 'GENERAL_INQUIRY'].indexOf(explicitType) < 0) explicitType = '';
    var isProductInquiry = explicitType === 'PRODUCT_INQUIRY'
      || normalizedSource === 'product_inquiry_fallback'
      || normalizedSource === 'ai_consultant_furniture_delivery_inquiry'
      || normalizedSource === 'ai_consultant_furniture_product_inquiry'
      || inquiryType === 'furniture / delivery inquiry';
    var isGeneralInquiry = explicitType === 'GENERAL_INQUIRY'
      || normalizedSource === 'ai_consultant_delivery_returns_general_inquiry';
    var isAiConsultation = !isGeneralInquiry && (explicitType === 'AI_CONSULTATION'
      || params.get('tiorConsultation') === '1'
      || normalizedSource.indexOf('ai_consultant_') === 0
      || normalizedSource.indexOf('ai_design') === 0
      || normalizedSource.indexOf('ai_interior_rendering') === 0
      || Boolean(params.get('projectType')));
    var requestType = isProductInquiry
      ? 'PRODUCT_INQUIRY'
      : isAiConsultation ? 'AI_CONSULTATION' : 'GENERAL_INQUIRY';
    var topic = requestType === 'PRODUCT_INQUIRY'
      ? 'FURNITURE_PRODUCT'
      : requestType === 'AI_CONSULTATION'
         ? 'CONSULTATION'
        : normalizedSource === 'ai_consultant_delivery_returns_general_inquiry'
          || normalizedSource === 'contact_delivery_installation'
          || normalizedSource === 'contact_delivery_order'
          ? 'DELIVERY_INSTALLATION'
          : normalizedSource === 'contact_other_inquiry' ? 'OTHER' : '';
    return {
      requestType: requestType,
      source: source,
      projectType: cleanParam(params.get('projectType') || params.get('inquiryType'), 120),
      products: readProducts(params),
      topic: topic,
      locked: Boolean(isProductInquiry && readProducts(params).length)
    };
  }

  function readProducts(params) {
    if (String(params.get('source') || '').toLowerCase() === 'product_inquiry_fallback') {
      try {
        var storedProducts = JSON.parse(window.sessionStorage.getItem('tior-product-inquiry-fallback-v1') || 'null');
        if (Array.isArray(storedProducts)) {
          var normalizedStoredProducts = storedProducts.slice(0, 20).map(normalizeProduct).filter(validProduct);
          if (normalizedStoredProducts.length) return normalizedStoredProducts;
        }
      } catch (error) {}
    }
    var productData = cleanParam(params.get('productData'), 10000);
    if (productData) {
      try {
        var decoded = JSON.parse(productData);
        if (Array.isArray(decoded)) return decoded.slice(0, 20).map(normalizeProduct).filter(validProduct);
      } catch (error) {}
    }

    var titles = splitParam(params.get('products') || params.get('product'));
    var productIds = splitParam(params.get('productIds') || params.get('productId'));
    var variantIds = splitParam(params.get('variantIds') || params.get('variantId'));
    return productIds.map(function (productId, index) {
      return normalizeProduct({
        productId: productId,
        variantId: variantIds[index] || '',
        title: titles[index] || titles[0] || 'Product inquiry',
        productUrl: params.get('productUrl') || '',
        imageUrl: params.get('imageUrl') || '',
        vendor: params.get('vendor') || '',
        sku: params.get('sku') || '',
        quantity: params.get('quantity') || 1,
        price: params.get('price') || '',
        priceLabel: params.get('priceLabel') || ''
      });
    }).filter(validProduct);
  }

  function normalizeProduct(value) {
    var product = value && typeof value === 'object' ? value : {};
    return {
      productId: cleanParam(product.productId, 128),
      variantId: cleanParam(product.variantId, 128),
      sku: cleanParam(product.sku, 255),
      title: cleanParam(product.title, 500) || 'Product inquiry',
      variantTitle: cleanParam(product.variantTitle, 255),
      vendor: cleanParam(product.vendor, 255),
      imageUrl: cleanParam(product.imageUrl, 2048),
      productUrl: cleanParam(product.productUrl, 2048),
      quantity: Math.max(1, Math.min(999, Math.floor(Number(product.quantity) || 1))),
      price: cleanParam(product.price, 120),
      priceLabel: cleanParam(product.priceLabel, 120)
    };
  }

  function validProduct(product) {
    return Boolean(product.productId && product.title);
  }

  function splitParam(value) {
    return String(value || '').split(',').map(function (item) { return item.trim(); }).filter(Boolean).slice(0, 20);
  }

  function cleanParam(value, maxLength) {
    return String(value || '').replace(/\u0000/g, '').replace(/[\r\n]/g, ' ').trim().slice(0, maxLength || 2000);
  }
})();
